import type { RouteMetaLogoutNavigation } from 'Auth'
import type { LocationQuery, RouteLocationNormalizedLoaded } from 'vue-router'
import type { CookieSerializeOptions } from 'cookie-es'
import { callWithNuxt } from '#app'
import type {
  AuthenticationAttemptRequest,
  TokenPackage,
  User,
  CertificateApplied,
} from '@/models'
import { AuthenticationAttempt } from '#components'

type AuthErrors = {
  login: any[]
  socialLogin: any[]
  refreshToken: string[]
  currentUser: string[]
}

export const useAuthStore = defineStore('auth.store', () => {
  /**
   * UTILS
   */
  const nuxt = useNuxtApp()
  const cookieStore = useCookiesStore()
  const locationStore = useLocationStore()
  const modalStore = useModalStore()

  /**
   * TOKENS
   */
  const tokenPackage = ref<Undefinable<TokenPackage>>(undefined)
  const errors = ref<AuthErrors>({
    login: [],
    socialLogin: [],
    refreshToken: [],
    currentUser: [],
  })

  const authToken = computed(() => tokenPackage.value?.accessToken)
  const refreshToken = computed(() => tokenPackage.value?.refreshToken)
  const isAuthorized = computed(() => !!authToken.value && !!refreshToken.value)

  const setTokenPackage = (value: TokenPackage) => {
    tokenPackage.value = value
  }

  const tryRefreshToken = async () => {
    try {
      const { data } = await nuxt.$api.resources.refreshAttempt.post({
        refreshToken: refreshToken.value!,
      })

      if (!data) return
      tokenPackage.value = data.refreshedTokenPackage

      const { accessToken, refreshToken: refreshTokenData } =
        data.refreshedTokenPackage

      setAuthCookies(accessToken, refreshTokenData)
    } catch (error) {
      errors.value.refreshToken.push(StringifyError(error))

      throw error
    }
  }

  /**
   * USER
   */
  const currentUser = ref<Undefinable<User>>(undefined)
  const userCertificates = ref<CertificateApplied[]>([])

  const updateCurrentUser = async () => {
    try {
      const { data } = await nuxt.$api.resources.profile.getCurrentUser()

      if (!data) return
      currentUser.value = data

      return currentUser.value
    } catch (error) {
      errors.value.currentUser.push(StringifyError(error))
    }
  }

  const updateUserCertificates = async () => {
    try {
      const { data } = await nuxt.$api.resources.profile.getUserCertificates()

      if (!data) return
      userCertificates.value = data

      return userCertificates.value
    } catch (error) {
      errors.value.currentUser.push(StringifyError(error))
    }
  }

  /**
   * LOGIN
   * @param data
   */
  const login = async (data: AuthenticationAttemptRequest) => {
    errors.value.login = []

    try {
      await nuxt.$api.resources.authenticationAttempt
        .post(data)
        .then(async (response) => {
          const tokenPackageData = response.data.authenticatedTokenPackage

          tokenPackage.value = tokenPackageData
          const { accessToken, refreshToken } = tokenPackageData

          setAuthCookies(accessToken, refreshToken)

          await updateCurrentUser()
        })
    } catch (error: any) {
      errors.value.login.push(StringifyError(error))
    }
  }

  const openLoginModal = (hideOAuth = false) => {
    return new Promise((resolve) => {
      modalStore.showModal({
        key: 'authenticationAttemptFormModal',
        modalConfig: {
          component: AuthenticationAttempt,
          isModalCloseButtonShown: true,
          modalSize: useDevice().isMobile ? 'bottom' : 'medium',
          showOverlay: true,
          modalComponentProps: {
            hideOAuth,
          },
          onModalClosed: () => {
            resolve(true)
          },
        },
      })
    })
  }

  const logout = () => {
    if (!isAuthorized.value) return

    tokenPackage.value = undefined
    currentUser.value = undefined

    setAuthCookies()

    return navigateToRouteLogoutPath(useRoute())
  }
  const openSocialEmailRequestModal = (query: LocationQuery) => {
    const SocialNetworkEmailRequestModal = defineAsyncComponent(
      () =>
        import(
          '~/components/entities/SocialNetwork/SocialNetworkEmailRequestModal.vue'
        ),
    )

    modalStore.showModal({
      key: 'socialNetworkEmailRequestModal',
      modalConfig: {
        component: SocialNetworkEmailRequestModal,
        isModalCloseButtonShown: true,
        modalSize: useDevice().isMobile ? 'bottom' : 'medium',
        showOverlay: true,
        modalComponentProps: {
          query,
        },
      },
    })
  }

  const socialEmail = ref<string>('')

  const openSocialPasswordModal = (query: LocationQuery) => {
    const SocialNetworkPasswordModal = defineAsyncComponent(
      () =>
        import(
          '@/components/entities/SocialNetwork/SocialNetworkPasswordModal.vue'
        ),
    )

    modalStore.showModal({
      key: 'socialNetworkPasswordModal',
      modalConfig: {
        component: SocialNetworkPasswordModal,
        isModalCloseButtonShown: true,
        modalSize: useDevice().isMobile ? 'bottom' : 'medium',
        showOverlay: true,
        modalComponentProps: {
          query,
        },
      },
    })
  }
  /**
   * oAuth2 login
   * @param query
   */
  const socialLogin = async (query: LocationQuery) => {
    errors.value.socialLogin = []
    try {
      const { data } = await nuxt.$api.resources.socialCallback.getToken(query)

      const { accessToken, refreshToken } = data

      setTokenPackage({
        accessToken,
        refreshToken,
      })

      setAuthCookies(accessToken, refreshToken)

      await updateCurrentUser()
    } catch (error: any) {
      if (error.httpStatus === 449) {
        // ошибка, когда отсутствует e-mail
        modalStore.closeModal('authenticationAttemptFormModal')
        openSocialEmailRequestModal(query)
      } else if (error.httpStatus === 409) {
        // ошибка, когда по переданному email уже существует аккаунт
        modalStore.closeModal('closeAll')
        openSocialPasswordModal(query)
      } else {
        errors.value.socialLogin.push(StringifyError(error))
      }
    }
  }

  /**
   * обрабатывает редирект при разлогине
   * если страницы закрыта, то на другую
   * если нет, то оставляет там же
   */
  const navigateToRouteLogoutPath = (route: RouteLocationNormalizedLoaded) => {
    const pageIsSecure = !!route.meta.secure

    if (!pageIsSecure) return

    const logoutNavigation: RouteMetaLogoutNavigation =
      <RouteMetaLogoutNavigation>route.meta.logoutNavigation ||
      <RouteMetaLogoutNavigation>{ path: '/', external: false }
    const logoutPath = logoutNavigation.external
      ? locationStore.getAbsoluteLink(logoutNavigation.path)
      : logoutNavigation.path

    try {
      return callWithNuxt(nuxt, navigateTo, [
        logoutPath,
        { external: logoutNavigation.external, redirectCode: 301 },
      ])
    } catch (error) {
      console.log(error)
    }
  }

  /**
   * COOKIES
   */
  const cookieParams = computed(
    (): CookieSerializeOptions => ({
      sameSite: 'lax',
      domain: cookieStore.cookiesDomain,
      path: '/',
      maxAge: 60 * 60 * 24 * 7,
      secure: true,
      httpOnly: false,
    }),
  )

  const accessTokenCookie = useCookie<string | undefined>(
    'JWT_TOKEN',
    cookieParams.value,
  )

  const refreshTokenCookie = useCookie<string | undefined>(
    'REFRESH_TOKEN',
    cookieParams.value,
  )

  const rootAccessToken = useCookie('JWT_TOKEN', {
    sameSite: 'lax',
    secure: true,
  })

  const rootRefreshToken = useCookie('REFRESH_TOKEN', {
    sameSite: 'lax',
    secure: true,
  })

  const setAuthCookies = (token?: string, refreshToken?: string) => {
    rootAccessToken.value = undefined
    rootRefreshToken.value = undefined

    accessTokenCookie.value = token
    refreshTokenCookie.value = refreshToken
  }

  return {
    tokenPackage,
    currentUser,
    errors,
    authToken,
    refreshToken,
    isAuthorized,
    userCertificates,
    socialEmail,
    login,
    socialLogin,
    openLoginModal,
    logout,
    updateCurrentUser,
    updateUserCertificates,
    navigateToRouteLogoutPath,
    setTokenPackage,
    tryRefreshToken,
    setAuthCookies,
    openSocialEmailRequestModal,
  }
})
